<script setup lang="ts">
  import { computed, watch, ref } from 'vue'
  import { useThemeColor } from '@/composables/useThemeColorV3'
  import { useTranslation } from '@/composables/useTranslation'
  import { useFieldValidation } from '@/composables/useQuestionnaire'
  import { AlertCircle } from 'lucide-vue-next'

  const props = defineProps({
    field: {
      type: Object,
      required: true
    },
    modelValue: {
      type: [String, Boolean],
      default: null
    },
    isPreviewMode: {
      type: Boolean,
      default: false
    },
    descriptionId: {
      type: String,
      required: false
    }
  })

  const emit = defineEmits(['update:modelValue', 'validation-error'])

  const selectedValue = ref(props.modelValue ?? '')
  const themeColor = computed(() => useThemeColor().value)

  const { t } = useTranslation({
    en: {
      noAnswer: 'No answer'
    },
    de: {
      noAnswer: 'keine Angabe'
    }
  })

  const { validationError, handleInputChange } = useFieldValidation(props, emit)

  const errorId = computed(() => `${props.field.uuid}-error`)
  const ariaDescribedBy = computed(() => {
    const ids: string[] = []
    if (props.descriptionId) ids.push(props.descriptionId)
    if (validationError.value) ids.push(errorId.value)
    return ids.length > 0 ? ids.join(' ') : undefined
  })

  function getRadioId(index: number) {
    return `${props.field.uuid}-option-${index}`
  }

  const options = computed(() => {
    if (props.field.type === 'yesno') {
      return [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nein' }
      ]
    }
    return props.field.choices.map((choice: any) => ({
      value: choice,
      label: choice === '' ? t('noAnswer') : choice
    }))
  })

  const isSelected = (optionValue: any) => {
    return selectedValue.value === optionValue
  }

  const handleRadioClick = (optionValue: any) => {
    if (props.isPreviewMode) return
    selectedValue.value = optionValue
    handleInputChange(optionValue)
  }

  // Ensure the correct option is selected when the component mounts or modelValue changes
  watch(
    () => props.modelValue,
    (newValue) => {
      selectedValue.value = newValue
    },
    { immediate: true }
  )
</script>

<template>
  <fieldset
    class="flex flex-col gap-2"
    :aria-describedby="ariaDescribedBy"
    :aria-invalid="!!validationError || undefined"
  >
    <legend class="sr-only">
      {{ field.label }}
    </legend>
    <div
      v-for="(option, index) in options"
      :key="option.value"
      class="flex items-center gap-2 rounded-lg p-2 transition-colors"
      :class="[
        isSelected(option.value) ? 'bg-gray-50' : 'bg-white',
        isPreviewMode ? 'cursor-default opacity-60' : 'cursor-pointer'
      ]"
    >
      <div class="relative">
        <input
          :id="getRadioId(index)"
          v-model="selectedValue"
          type="radio"
          :name="field.uuid"
          :value="option.value"
          :disabled="isPreviewMode"
          class="sr-only"
          @change="handleRadioClick(selectedValue)"
        />
        <div
          class="flex h-4 w-4 items-center justify-center rounded-full border transition-colors"
          :class="[
            isSelected(option.value)
              ? `bg-${themeColor}-600 border-${themeColor}-600`
              : 'border-gray-300 bg-white',
            isPreviewMode ? 'cursor-default' : 'cursor-pointer'
          ]"
          aria-hidden="true"
          :data-cy="`RadioButtonV3Radio-${field.uuid}-${index}`"
          @click="handleRadioClick(option.value)"
        >
          <div
            v-if="isSelected(option.value)"
            class="h-2 w-2 rounded-full bg-white"
          ></div>
        </div>
      </div>
      <label
        :for="getRadioId(index)"
        class="flex-1 text-sm font-medium text-gray-900"
        :class="isPreviewMode ? 'cursor-default' : 'cursor-pointer'"
      >
        {{ option.label }}
      </label>
    </div>
    <div
      v-if="validationError"
      :id="errorId"
      class="mt-1 flex items-center text-sm text-red-600"
      aria-live="polite"
    >
      <AlertCircle
        class="mr-1 h-4 w-4 flex-shrink-0"
        aria-hidden="true"
      />
      {{ validationError }}
    </div>
  </fieldset>
</template>

<style scoped>
  label {
    display: flex;
    align-items: center;
    padding: 5px 1rem 5px 5px;
    border-radius: 3px;
    margin: 5px 0;
    cursor: pointer;
  }
</style>
